import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isAddressValidationBlocking } from "@/components/common/addressValidation";
import { ConfirmModal } from "@/components/ui";
import type { AddressValidationState } from "@/types";

interface PendingConfirm {
  message: string;
}

export function useInvalidAddressSaveConfirm() {
  const { t } = useTranslation("common");
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingProceedRef = useRef<(() => void) | null>(null);

  const requestInvalidAddressSaveConfirm = useCallback(
    (
      hasPhysicalAddress: boolean,
      validation: AddressValidationState,
      message: string,
      onProceed: () => void,
    ) => {
      if (!isAddressValidationBlocking(hasPhysicalAddress, validation)) {
        onProceed();
        return;
      }
      pendingProceedRef.current = onProceed;
      setPending({ message });
    },
    [],
  );

  const dismissConfirm = useCallback(() => {
    pendingProceedRef.current = null;
    setPending(null);
  }, []);

  const acceptConfirm = useCallback(() => {
    const proceed = pendingProceedRef.current;
    pendingProceedRef.current = null;
    setPending(null);
    proceed?.();
  }, []);

  const confirmModal = (
    <ConfirmModal
      closeLabel={t("close")}
      cancelText={t("cancel")}
      isOpen={pending !== null}
      onClose={dismissConfirm}
      onConfirm={acceptConfirm}
      title={t("addressValidation.saveConfirmTitle")}
      message={pending?.message ?? ""}
      confirmText={t("addressValidation.saveConfirmButton")}
    />
  );

  return { requestInvalidAddressSaveConfirm, confirmModal };
}
